// Running a view's SQL in the browser.
//
// A port of stack-pkg/src/libs/site-view/index.js, which is the piece of the
// archived product worth having: parse the query, work out which tables it
// reads, load those and only those, and execute — all client-side, over rows the
// browser already has.
//
// In app/shared rather than in app/code/src because the read-only viewer app a
// share link opens runs the SAME query against the same tables. There is no
// package.json here and no node_modules above it, so the bare imports below
// resolve through whichever app is building — the arrangement wfws' app/shared
// documents and app/code/craco.config.js declares the alias for.
//
// ALASQL IS LOADED DYNAMICALLY, which is not an optimisation. It is ~516 kB
// minified, and neither app's first screen is a query — the editor opens on a
// table listing and the viewer on a view's own page, which needs the definition
// before it needs the engine. A static import would put all of it in `main.js`,
// parsed and executed before anything renders. Warmed on module load below, so
// by the time a person reaches a Run button it has long since arrived.
import { tableIdsOf } from "@common/code/sql/tables";
import { register } from "./functions";

let enginePromise = null;

const engine = () => {
  if (!enginePromise) {
    enginePromise = import("alasql").then((mod) => {
      const alasql = mod.default || mod;
      register(alasql);
      return alasql;
    });
  }
  return enginePromise;
};

// Fetched as soon as anything imports this module, the same warming
// @repoflow/components/code does for its CodeMirror chunk and for the same
// reason: a promise that has already settled resolves synchronously, so the
// first query does not wait on the network.
engine();

/** Parse only — what the api needs to derive a view's `tables[]`. */
export const parse = async (sql) => {
  const alasql = await engine();
  const ast = alasql.parse(sql);
  if (!ast || !ast.statements || ast.statements.length !== 1) {
    // One statement per view. Not a limitation being apologised for: a view IS a
    // query, and a semicolon-separated pair has no single result to render.
    throw new Error("sql.statements.expected-one");
  }
  return ast.statements[0];
};

/** The tables a query reads, derived rather than declared. */
export const tablesOf = async (sql) => tableIdsOf(await parse(sql));

/**
 * Run a view's query.
 *
 * `loadTable(tableId)` returns `{ columns, entries }` — the caller decides where
 * from, which is what lets the editor read them over GraphQL and the viewer read
 * them from the distribution's rows route without this file knowing the
 * difference.
 *
 * A DATABASE PER RUN, DROPPED AFTERWARDS.
 *
 * The archive kept one database per tenant domain and dropped each table on a
 * 60-second `setTimeout`. That leaks a timer per load, and races the next query
 * for the same id — a run starting at t+59s could have its table pulled out from
 * under it. alasql's `databases`, `tables` and `fn` are process-wide, so two
 * components querying at once is the ordinary case that has to be safe.
 *
 * A fresh, uniquely-named database costs one CREATE and makes each run
 * independent. The `finally` drops it whether the query threw or not.
 */
let runSeq = 0;

export const run = async (sql, { loadTable, params } = {}) => {
  const alasql = await engine();
  const statement = await parse(sql);
  const tableIds = tableIdsOf(statement);

  const dbName = `v${Date.now().toString(36)}_${(runSeq += 1)}`;
  alasql(`CREATE DATABASE \`${dbName}\``);
  const previous = alasql.useid;

  try {
    alasql(`USE \`${dbName}\``);

    for (const tableId of tableIds) {
      const loaded = await loadTable(tableId);
      if (!loaded) {
        // Named, because this is the error a person can act on: the view refers
        // to a table that is gone, or was never theirs.
        throw new Error(`table.missing:${tableId}`);
      }
      alasql(`CREATE TABLE \`${tableId}\``);
      // Rows attached directly rather than INSERTed: alasql reads
      // `tables[id].data` as the backing array, and an INSERT per row of a
      // 10,000-row table is the slowest possible way to say the same thing.
      alasql.tables[tableId].data = loaded.entries || [];
    }

    // BOUND, never interpolated.
    //
    // The archive did `SiteRenderUtils.resolve(query, context)` — string
    // substitution of `{{params.x}}` into the SQL BEFORE parsing — with the
    // values coming off a public page's route. That is injection by
    // construction. alasql takes positional parameters; a view that wants one
    // writes `?` and the caller passes the value.
    const rows = alasql(sql, params || []);
    return { rows, statement, tables: tableIds };
  } finally {
    try {
      if (previous) alasql(`USE \`${previous}\``);
      alasql(`DROP DATABASE IF EXISTS \`${dbName}\``);
    } catch {
      // A failed cleanup must not replace the caller's error with its own.
    }
  }
};

export default { parse, tablesOf, run };
